import React from 'react';
import type { OfficeAgentCode, OfficeAgentDto } from '../../../../shared/types';

/**
 * Vista «Ufficio»: una stanza con tre scrivanie. Ogni assistente lavora alla sua: quando lavora scrive
 * alla tastiera e lo schermo scorre, quando aspetta beve il caffè, se c'è un problema compare un «!»,
 * se è spento la sedia è vuota. Sopra la testa c'è un fumetto con quello che sta facendo adesso.
 * La finestra mostra il giorno o la notte e l'orologio segna l'ora vera.
 */

export const AGENT_COLOR: Record<OfficeAgentCode, string> = {
  segreteria: '#e5487a',
  tecnico: '#3b82f6',
  opportunita: '#e8a317',
};

const W = 1200;
const H = 520;
const DESK_X: Record<OfficeAgentCode, number> = { segreteria: 210, tecnico: 600, opportunita: 990 };

/**
 * Il «grado» di un assistente (da come lavora): Stagista, Operativo, Fermo.
 * Il nome dice a che punto è; il suggerimento spiega tutto in parole semplici.
 * Quando crescerà si potranno aggiungere altri gradi (es. «Senior»).
 */
export function modeInfo(a: OfficeAgentDto): { label: string; tip: string; content: { title: string; lines: string[]; hint?: string } } {
  const f = a.code !== 'tecnico'; // femminile: Segreteria e Opportunità
  const make = (label: string, lines: string[], hint?: string) => ({ label, tip: lines.join(' '), content: { title: label, lines, hint } });
  if (a.mode === 'spento') return make(f ? 'Ferma' : 'Fermo', [`${f ? 'È spenta' : 'È spento'}: non lavora.`], `Per ${f ? 'riaccenderla' : 'riaccenderlo'}, apri la sua scheda.`);
  if (a.mode === 'prova')
    return make(
      'Stagista',
      [
        'Sta ancora imparando.',
        'Legge la posta e scrive cosa farebbe, ma non sposta mail e non scrive in agenda.',
        'Tu controlli le sue scelte. Quando in Pagella una materia va bene, la lasci fare da sola.',
      ],
      'Quando ti fidi di tutto, passala a «Operativa».',
    );
  if (a.code === 'segreteria')
    return make('Operativa', ['È attiva: smista la posta e inserisce gli impegni in agenda.', 'Le risposte ai clienti partono solo con il tuo sì.'], 'Ogni cosa che fa resta in «Cosa hanno fatto»: lì la puoi correggere.');
  if (a.code === 'tecnico') return make('Operativo', ['È attivo: controlla siti e server ogni 5 minuti.', 'Se qualcosa si ferma, ti avvisa.']);
  return make('Operativa', ['È attiva: cerca bandi e voucher alle 9 e alle 14, dal lunedì al venerdì.', 'Quelli trovati vanno in «Da approvare».']);
}

export function stateLabel(a: OfficeAgentDto): string {
  if (a.mode === 'spento') return 'Spento';
  if (a.state === 'lavora') return 'Al lavoro';
  if (a.state === 'errore') return 'C’è un problema';
  return 'In attesa';
}

const pct = (v: number, of: number) => `${(v / of) * 100}%`;

function clockHands(now: Date) {
  const h = now.getHours() % 12;
  const m = now.getMinutes();
  return { hour: h * 30 + m * 0.5, minute: m * 6 };
}

function Hair({ code }: { code: OfficeAgentCode }) {
  if (code === 'segreteria') {
    // capelli raccolti con lo chignon e cuffia con microfono
    return (
      <g>
        <circle cx="0" cy="150" r="16" fill="var(--of-hair-2)" />
        <path d="M-33 192 C-36 160 -18 146 0 146 C18 146 36 160 33 192 C26 176 14 168 0 168 C-14 168 -26 176 -33 192 Z" fill="var(--of-hair-2)" />
        <path d="M-35 192 A35 35 0 0 1 35 192" fill="none" stroke="var(--of-ink)" strokeWidth="4" strokeLinecap="round" />
        <rect x="-41" y="184" width="10" height="18" rx="4" fill="var(--of-ink)" />
        <path d="M-36 200 Q-30 218 -12 214" fill="none" stroke="var(--of-ink)" strokeWidth="3" strokeLinecap="round" />
        <circle cx="-11" cy="214" r="3.5" fill="var(--of-ink)" />
      </g>
    );
  }
  if (code === 'tecnico') {
    // capelli corti e occhiali
    return (
      <g>
        <path d="M-33 186 C-35 158 -16 150 0 150 C20 150 36 160 33 186 C30 172 20 166 4 166 C-10 166 -24 170 -33 186 Z" fill="var(--of-hair)" />
        <g fill="none" stroke="var(--of-ink)" strokeWidth="2.6">
          <circle cx="-12" cy="192" r="8.5" />
          <circle cx="12" cy="192" r="8.5" />
          <path d="M-3.5 192 H3.5" />
        </g>
      </g>
    );
  }
  // ricci
  return (
    <g fill="var(--of-hair-3)">
      {[
        [-26, 172, 11],
        [-14, 160, 12],
        [2, 155, 13],
        [17, 160, 12],
        [28, 172, 11],
        [-31, 186, 8],
        [32, 186, 8],
      ].map(([x, y, r], i) => (
        <circle key={i} cx={x} cy={y} r={r} />
      ))}
    </g>
  );
}

function Prop({ code }: { code: OfficeAgentCode }) {
  if (code === 'segreteria') {
    // vaschetta della posta con le lettere; quando lavora una busta vola verso lo schermo
    return (
      <g>
        <g className="of-tray">
          <path d="M92 300 H168 L162 332 H98 Z" fill="var(--of-prop)" />
          <rect x="100" y="286" width="58" height="18" rx="2" fill="#fff" stroke="var(--of-line)" />
          <path d="M100 286 L129 298 L158 286" fill="none" stroke="var(--of-line)" />
          <rect x="104" y="278" width="52" height="12" rx="2" fill="#fff8e6" stroke="var(--of-line)" />
        </g>
        <g className="of-fly">
          <rect x="-11" y="-7" width="22" height="14" rx="2" fill="#fff" stroke="var(--of-line)" />
          <path d="M-11 -7 L0 1 L11 -7" fill="none" stroke="var(--of-line)" />
        </g>
      </g>
    );
  }
  if (code === 'tecnico') {
    // armadietto del server con le lucine
    return (
      <g>
        <rect x="96" y="238" width="66" height="94" rx="7" fill="var(--of-rack)" />
        {[0, 1, 2].map((i) => (
          <g key={i}>
            <rect x="104" y={248 + i * 27} width="50" height="19" rx="3" fill="var(--of-rack-2)" />
            <circle className={`of-led l${i}`} cx="113" cy={257.5 + i * 27} r="3.2" />
            <circle className={`of-led l${i + 3}`} cx="123" cy={257.5 + i * 27} r="3.2" />
            <rect x="132" y={255 + i * 27} width="16" height="4" rx="2" fill="var(--of-rack)" />
          </g>
        ))}
      </g>
    );
  }
  // mappamondo e lente che cerca su un foglio
  return (
    <g>
      <rect x="70" y="318" width="46" height="12" rx="2" fill="#fff" stroke="var(--of-line)" transform="rotate(-6 93 324)" />
      <g className="of-globe">
        <path d="M150 332 H122 L128 326 H144 Z" fill="var(--of-prop)" />
        <rect x="134" y="306" width="4" height="22" fill="var(--of-prop)" />
        <circle cx="136" cy="282" r="26" fill="#5fb3e6" />
        <clipPath id="of-globe-clip">
          <circle cx="136" cy="282" r="26" />
        </clipPath>
        <g clipPath="url(#of-globe-clip)">
          <g className="of-globe-land">
            <path d="M96 268 q10 -8 22 0 q6 10 -4 18 q-12 4 -18 -6 Z M128 290 q12 -6 18 4 q-2 12 -14 10 Z M150 262 q10 -2 14 8 q-6 8 -14 2 Z M176 268 q10 -8 22 0 q6 10 -4 18 q-12 4 -18 -6 Z M208 290 q12 -6 18 4 q-2 12 -14 10 Z" fill="#79c37a" />
          </g>
        </g>
        <circle cx="136" cy="282" r="26" fill="none" stroke="rgba(0,0,0,.12)" />
        <path d="M108 282 A28 28 0 0 1 164 282" fill="none" stroke="var(--of-prop)" strokeWidth="3" />
      </g>
      <g className="of-lens">
        <circle cx="90" cy="312" r="9" fill="rgba(255,255,255,.45)" stroke="var(--of-ink)" strokeWidth="3" />
        <path d="M97 319 L106 328" stroke="var(--of-ink)" strokeWidth="4" strokeLinecap="round" />
      </g>
    </g>
  );
}

function Workstation({ a }: { a: OfficeAgentDto }) {
  const x = DESK_X[a.code];
  const off = a.mode === 'spento';
  const cls = `of-ws ${off ? 'off' : a.state}`;
  return (
    <g className={cls} transform={`translate(${x} 0)`} style={{ '--ac': AGENT_COLOR[a.code] } as React.CSSProperties}>
      <ellipse cx="0" cy="452" rx="190" ry="12" fill="var(--of-shadow)" />
      {/* sedia */}
      <rect x="-60" y="196" width="120" height="146" rx="22" fill="var(--of-chair)" />
      {/* persona */}
      <g className="of-person">
        <rect x="-48" y="238" width="96" height="110" rx="26" fill="var(--ac)" />
        <path d="M-14 238 L0 256 L14 238" fill="rgba(255,255,255,.85)" />
        <rect x="-9" y="212" width="18" height="30" rx="6" fill="var(--of-skin-2)" />
        <g className="of-head">
          <circle cx="0" cy="190" r="32" fill="var(--of-skin)" />
          <Hair code={a.code} />
          <g className="of-eyes">
            <circle cx="-11" cy="193" r="3.2" fill="var(--of-ink)" />
            <circle cx="11" cy="193" r="3.2" fill="var(--of-ink)" />
          </g>
          <path className="of-mouth" d="M-9 206 Q0 213 9 206" fill="none" stroke="var(--of-ink)" strokeWidth="2.6" strokeLinecap="round" />
          <circle cx="-20" cy="203" r="4.5" fill="#f4a0a0" opacity=".45" />
          <circle cx="20" cy="203" r="4.5" fill="#f4a0a0" opacity=".45" />
        </g>
        {/* braccia e mani sulla tastiera */}
        <g className="of-arm left">
          <path d="M-40 256 Q-60 296 -26 324" fill="none" stroke="var(--ac)" strokeWidth="17" strokeLinecap="round" />
          <circle cx="-24" cy="324" r="8.5" fill="var(--of-skin)" />
        </g>
        <g className="of-arm right">
          <path d="M40 256 Q60 296 26 324" fill="none" stroke="var(--ac)" strokeWidth="17" strokeLinecap="round" />
          <circle cx="24" cy="324" r="8.5" fill="var(--of-skin)" />
        </g>
      </g>
      {/* scrivania */}
      <rect x="-180" y="328" width="360" height="15" rx="5" fill="var(--of-desk)" />
      <rect x="-168" y="343" width="336" height="100" rx="6" fill="var(--of-desk-2)" />
      <rect x="-150" y="360" width="300" height="3" rx="1.5" fill="rgba(0,0,0,.06)" />
      <rect x="-160" y="372" width="96" height="58" rx="5" fill="rgba(255,255,255,.08)" stroke="rgba(0,0,0,.07)" />
      <rect x="64" y="372" width="96" height="58" rx="5" fill="rgba(255,255,255,.08)" stroke="rgba(0,0,0,.07)" />
      <rect x="-128" y="398" width="32" height="6" rx="3" fill="rgba(0,0,0,.14)" />
      <rect x="96" y="398" width="32" height="6" rx="3" fill="rgba(0,0,0,.14)" />
      <rect x="-50" y="321" width="100" height="9" rx="3" fill="var(--of-keys)" />
      {/* schermo */}
      <g className="of-monitor">
        <rect x="-140" y="322" width="46" height="7" rx="2" fill="var(--of-prop)" />
        <rect x="-121" y="300" width="8" height="24" fill="var(--of-prop)" />
        <rect x="-172" y="232" width="104" height="72" rx="8" fill="var(--of-bezel)" />
        <clipPath id={`of-screen-${a.code}`}>
          <rect x="-166" y="238" width="92" height="60" rx="4" />
        </clipPath>
        <rect className="of-screen" x="-166" y="238" width="92" height="60" rx="4" />
        <g clipPath={`url(#of-screen-${a.code})`}>
          <g className="of-lines">
            {Array.from({ length: 12 }, (_, i) => (
              <rect key={i} x="-158" y={246 + i * 10} width={[60, 44, 70, 36, 56, 66, 40, 62, 50, 70, 30, 58][i]} height="4" rx="2" />
            ))}
          </g>
          <g className="of-bang">
            <circle cx="-120" cy="268" r="14" fill="#fff" />
            <rect x="-122" y="258" width="4" height="13" rx="2" fill="var(--bad)" />
            <circle cx="-120" cy="276" r="2.4" fill="var(--bad)" />
          </g>
        </g>
      </g>
      <Prop code={a.code} />
      {/* tazza del caffè (quando aspetta) */}
      <g className="of-mug">
        <path className="of-steam s1" d="M64 300 q-5 -8 0 -15 q5 -7 0 -14" fill="none" stroke="var(--of-steam)" strokeWidth="2.6" strokeLinecap="round" />
        <path className="of-steam s2" d="M74 302 q-5 -8 0 -15 q5 -7 0 -14" fill="none" stroke="var(--of-steam)" strokeWidth="2.6" strokeLinecap="round" />
        <rect x="56" y="306" width="26" height="24" rx="6" fill="#fff" stroke="var(--of-line)" />
        <path d="M82 312 q10 0 10 7 q0 7 -10 7" fill="none" stroke="var(--of-line)" strokeWidth="3" />
      </g>
      {/* problema: punto esclamativo sopra la testa */}
      <g className="of-alert">
        <circle cx="54" cy="172" r="15" fill="var(--bad)" />
        <rect x="51.8" y="162" width="4.4" height="13" rx="2.2" fill="#fff" />
        <circle cx="54" cy="180.5" r="2.6" fill="#fff" />
      </g>
      {/* spento: cartellino sul tavolo */}
      <g className="of-offsign">
        <rect x="-36" y="296" width="72" height="28" rx="6" fill="var(--of-bezel)" />
        <text x="0" y="315" textAnchor="middle" fontSize="14" fontWeight="600" fill="#fff">
          Spento
        </text>
      </g>
      {a.mode === 'prova' && !off && (
        <g>
          <rect x="-48" y="380" width="96" height="28" rx="14" fill="var(--of-trial)" />
          <text x="0" y="399" textAnchor="middle" fontSize="14" fontWeight="600" fill="var(--of-trial-ink)">
            In prova
          </text>
        </g>
      )}
      <title>{`${a.name}: ${stateLabel(a)}. ${a.activity}`}</title>
    </g>
  );
}

function Room({ now }: { now: Date }) {
  const h = now.getHours();
  const day = h >= 7 && h < 19;
  const { hour, minute } = clockHands(now);
  return (
    <g>
      <rect x="0" y="0" width={W} height="330" fill="url(#of-wall)" />
      <rect x="0" y="330" width={W} height={H - 330} fill="url(#of-floor)" />
      <rect x="0" y="326" width={W} height="8" fill="var(--of-base)" />
      {Array.from({ length: 9 }, (_, i) => (
        <path key={i} d={`M${i * 150 - 40} ${H} L${i * 150 + 40} 334`} stroke="var(--of-plank)" strokeWidth="2" />
      ))}
      {/* finestra: giorno o notte secondo l'ora vera */}
      <g>
        <rect x="452" y="34" width="296" height="150" rx="10" fill="var(--of-frame)" />
        <rect x="462" y="44" width="276" height="130" rx="5" fill={day ? 'url(#of-sky-day)' : 'url(#of-sky-night)'} />
        {day ? (
          <g>
            <circle cx="690" cy="80" r="18" fill="#ffd45c" />
            <g className="of-cloud">
              <ellipse cx="540" cy="96" rx="30" ry="12" fill="#fff" opacity=".9" />
              <ellipse cx="562" cy="88" rx="20" ry="12" fill="#fff" opacity=".9" />
            </g>
            <path d="M462 150 Q540 128 600 146 T738 140 V174 H462 Z" fill="#9cc98a" opacity=".8" />
          </g>
        ) : (
          <g>
            <circle cx="680" cy="82" r="16" fill="#f4f1d8" />
            <circle cx="687" cy="77" r="14" fill="#26335a" />
            {[
              [500, 70],
              [540, 96],
              [580, 60],
              [620, 100],
              [520, 130],
              [650, 130],
            ].map(([x, y], i) => (
              <circle key={i} className={`of-star t${i % 3}`} cx={x} cy={y} r="2" fill="#fff" />
            ))}
            <path d="M462 150 Q540 128 600 146 T738 140 V174 H462 Z" fill="#1d2a46" />
          </g>
        )}
        <rect x="597" y="44" width="6" height="130" fill="var(--of-frame)" />
        <rect x="462" y="106" width="276" height="6" fill="var(--of-frame)" />
      </g>
      {/* orologio con l'ora vera */}
      <g transform="translate(405 92)">
        <circle r="34" fill="var(--of-frame)" />
        <circle r="28" fill="#fff" />
        {Array.from({ length: 12 }, (_, i) => (
          <rect key={i} x="-1" y="-25" width="2" height={i % 3 === 0 ? 6 : 3} fill="#5b6475" transform={`rotate(${i * 30})`} />
        ))}
        <rect x="-1.8" y="-15" width="3.6" height="17" rx="1.8" fill="#2b3142" transform={`rotate(${hour})`} />
        <rect x="-1.2" y="-22" width="2.4" height="24" rx="1.2" fill="#2b3142" transform={`rotate(${minute})`} />
        <circle r="3" fill="#e5487a" />
      </g>
      {/* quadro con il segno Tecnoside */}
      <g transform="translate(800 64)">
        <rect x="0" y="0" width="98" height="82" rx="6" fill="var(--of-frame)" />
        <rect x="7" y="7" width="84" height="68" rx="3" fill="#1d2140" />
        <g transform="translate(49 41) scale(1.9) translate(-12 -12)">
          <path
            fill="#fff"
            fillRule="evenodd"
            d="M12,1 21.53,6.5 21.53,17.5 12,23 2.47,17.5 2.47,6.5Z M12,4.7 18.32,8.35 18.32,15.65 12,19.3 5.68,15.65 5.68,8.35Z"
          />
          <polygon fill="#2dd4bf" points="12,7.6 15.81,9.8 15.81,14.2 12,16.4 8.19,14.2 8.19,9.8" />
        </g>
      </g>
      {/* mensola con i raccoglitori */}
      <g transform="translate(60 120)">
        <rect x="0" y="62" width="120" height="8" rx="3" fill="var(--of-desk)" />
        {[
          ['#3b82f6', 0],
          ['#e5487a', 18],
          ['#e8a317', 36],
          ['#16a36a', 54],
        ].map(([c, dx], i) => (
          <rect key={i} x={8 + (dx as number)} y={i === 2 ? 22 : 14} width="15" height={i === 2 ? 40 : 48} rx="2" fill={c as string} />
        ))}
        <path d="M84 62 L106 18 L114 22 L94 62 Z" fill="#8b5cf6" />
      </g>
      {/* pianta */}
      <g transform="translate(1135 330)">
        <path d="M-22 80 H22 L16 120 H-16 Z" fill="#c87a4f" />
        <g className="of-plant">
          <path d="M0 80 C-30 50 -40 20 -26 0 C-10 24 -4 50 0 80" fill="#3f9b5f" />
          <path d="M0 80 C30 46 42 18 28 -6 C10 22 4 50 0 80" fill="#4fb36f" />
          <path d="M0 80 C-8 40 0 10 8 -20 C16 14 10 50 0 80" fill="#3a8c55" />
        </g>
      </g>
    </g>
  );
}

export function OfficeScene({ agents, now, onPick }: { agents: OfficeAgentDto[]; now: Date; onPick: (code: OfficeAgentCode) => void }) {
  return (
    <div className="of-scene" style={{ aspectRatio: `${W} / ${H}` }}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="L'ufficio con la squadra al lavoro">
        <defs>
          <linearGradient id="of-wall" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="var(--of-wall-1)" />
            <stop offset="1" stopColor="var(--of-wall-2)" />
          </linearGradient>
          <linearGradient id="of-floor" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="var(--of-floor-1)" />
            <stop offset="1" stopColor="var(--of-floor-2)" />
          </linearGradient>
          <linearGradient id="of-sky-day" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#7cc4f2" />
            <stop offset="1" stopColor="#d4ecfb" />
          </linearGradient>
          <linearGradient id="of-sky-night" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#0f1a36" />
            <stop offset="1" stopColor="#26335a" />
          </linearGradient>
        </defs>
        <Room now={now} />
        {agents.map((a) => (
          <Workstation key={a.code} a={a} />
        ))}
      </svg>
      {agents.map((a) => {
        const x = DESK_X[a.code];
        const waiting = a.mode !== 'spento' && a.state === 'attesa';
        return (
          <React.Fragment key={a.code}>
            <div className={`of-bubble ${a.mode === 'spento' ? 'off' : a.state}`} style={{ left: pct(x, W), top: pct(140, H) }}>
              <span className="of-bubble-text">{a.progress ? `${a.activity}` : a.activity}</span>
              {a.progress && (
                <span className="of-bubble-bar">
                  <i style={{ width: `${Math.round((a.progress.done / Math.max(1, a.progress.total)) * 100)}%` }} />
                </span>
              )}
              {waiting && a.nextRunAt && <span className="of-bubble-next">Prossimo giro alle {new Date(a.nextRunAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>}
            </div>
            <button
              type="button"
              className="of-hit"
              style={{ left: pct(x - 185, W), width: pct(370, W), top: pct(150, H), height: pct(300, H) }}
              onClick={() => onPick(a.code)}
              aria-label={`${a.name}: ${stateLabel(a)}. Apri la scheda`}
            />
            <button type="button" className="of-plate" style={{ left: pct(x, W), top: pct(452, H), '--ac': AGENT_COLOR[a.code] } as React.CSSProperties} onClick={() => onPick(a.code)}>
              <b>{a.name}</b>
              <span className={`of-state ${a.mode === 'spento' ? 'off' : a.state}`}>
                <i />
                {stateLabel(a)}
              </span>
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
}

/** Cosa fa l'agente, in parole semplici e professionali (vale anche se il server ha ancora il testo vecchio). */
export function roleText(a: { code: string; role: string }): string {
  if (a.code === 'segreteria') return 'Gestisce la posta, prepara le risposte e organizza l’agenda';
  return a.role;
}
